/**
 * Heuristic language detection by stopword frequency.
 * @module lib/language-detect
 */

/** Result of language detection. */
export interface LanguageResult {
  lang: string;
  confidence: number;
  scores: Record<string, number>;
}

/**
 * Normalizes text by collapsing whitespace and trimming.
 */
export function normaliseText(text: string): string {
  return (text ?? "").toString().normalize("NFKC").replace(/\s+/g, " ").trim();
}

/**
 * Tokenizes text into lowercase words for analysis.
 */
function tokenize(text: string): string[] {
  return normaliseText(text)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s'-]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Counts how many tokens match a set of stopwords.
 */
function countMatches(tokens: string[], stopwordsSet: Set<string>): number {
  return tokens.filter((t) => stopwordsSet.has(t)).length;
}

/** Stopwords by language for heuristic language detection */
export const LANGUAGE_STOPWORDS: Record<string, Set<string>> = {
  en: new Set([
    "the",
    "and",
    "to",
    "of",
    "in",
    "for",
    "with",
    "on",
    "by",
    "as",
    "from",
    "that",
    "this",
    "these",
    "those",
    "will",
    "can",
    "be",
    "is",
    "are",
    "an",
    "a",
    "at",
    "or",
    "into",
    "using",
  ]),
  ga: new Set([
    "agus",
    "an",
    "na",
    "ar",
    "le",
    "go",
    "i",
    "is",
    "ní",
    "mar",
    "don",
    "do",
    "seo",
    "sin",
    "atá",
    "bhfuil",
  ]),
  fr: new Set([
    "le",
    "la",
    "les",
    "et",
    "de",
    "des",
    "du",
    "un",
    "une",
    "dans",
    "pour",
    "avec",
    "sur",
    "par",
    "est",
    "être",
    "au",
  ]),
  es: new Set([
    "el",
    "la",
    "los",
    "las",
    "y",
    "de",
    "del",
    "un",
    "una",
    "en",
    "para",
    "con",
    "por",
    "es",
    "ser",
    "al",
    "que",
  ]),
  de: new Set([
    "der",
    "die",
    "das",
    "und",
    "zu",
    "von",
    "mit",
    "für",
    "im",
    "auf",
    "ist",
    "sein",
    "eine",
    "ein",
    "den",
    "dem",
  ]),
};

/**
 * Detects the language of text using stopword frequency heuristics.
 */
export function detectLanguage(
  text: string,
  { minTokens = 6 }: { minTokens?: number } = {},
): LanguageResult {
  const tokens = tokenize(text);
  if (tokens.length < minTokens) {
    return { lang: "unknown", confidence: 0, scores: {} };
  }

  const scores: Record<string, number> = {};
  Object.entries(LANGUAGE_STOPWORDS).forEach(([lang, sw]) => {
    scores[lang] = countMatches(tokens, sw);
  });

  const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const [bestLang, bestScore] = ranked[0] ?? ["unknown", 0];
  const secondScore = ranked[1]?.[1] ?? 0;

  const confidence = bestScore === 0 ? 0 : (bestScore - secondScore) / Math.max(1, tokens.length);

  if (bestScore < 2) {
    return { lang: "unknown", confidence: 0, scores };
  }

  return { lang: bestLang, confidence, scores };
}
